import { useRef, forwardRef } from "react";

import {
  Box,
  List,
  CardMedia,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Icon,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import ChromeReaderModeIcon from "@mui/icons-material/ChromeReaderMode";
import CalculateIcon from "@mui/icons-material/Calculate";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import KeyIcon from "@mui/icons-material/Key";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import LocalCafeRoundedIcon from "@mui/icons-material/LocalCafeRounded";
import SportsTennisRoundedIcon from "@mui/icons-material/SportsTennisRounded";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import { userState, IMenuItem } from "../interface/MainInterface";
import { HttpMainApi } from "../interface/main-api";
import PopCreatePress from "../page/module/PopCreatePress";
interface propsType {
  userState: userState;
  selectedMenu: string;
  move: any;
  closeMenu: () => void;
}

interface IMenuGroup {
  name: string;
  menuList: IMenuItem[];
  access: "management" | "authenticated" | "superAdmin";
}

// 관리자 메뉴 : 그룹별 접근
const MANAGEMENT_MENU_GROUPS: Omit<IMenuGroup, "access">[] = [
  {
    name: "회원 관리",
    menuList: [
      {
        name: "회원관리",
        iconType: "img",
        icon: "/images/icon-search.png",
        path: "/userlist",
      },
      {
        name: "지문 관리",
        iconType: "mui-icon",
        icon: <FingerprintIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/userfingerlist",
      },
    ],
  },
  {
    name: "커뮤니티 관리",
    menuList: [
      {
        name: "통합청구서",
        iconType: "mui-icon",
        icon: <CalculateIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/integratedaccountlist",
        adminOnly: true,
      },
      {
        name: "관리비 청구 상세",
        iconType: "mui-icon",
        icon: <ChromeReaderModeIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/accountdetaillist",
        adminOnly: true,
      },
      {
        name: "예약관리",
        iconType: "mui-icon",
        icon: <PendingActionsIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/adminreserv",
      },
      {
        name: "북카페 예약 현황",
        iconType: "mui-icon",
        icon: <LocalCafeRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/bookcafereservationstatus",
        adminOnly: true,
      },
      {
        name: "락커 비밀번호 관리",
        iconType: "mui-icon",
        icon: <KeyIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/lockerpassword",
      },
    ],
  },
  {
    name: "청소 로봇 관리",
    menuList: [
      {
        name: "청소기 대리 예약",
        iconType: "mui-icon",
        icon: <CleaningServicesIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/robotcleanerrental",
        adminOnly: true,
      },
      {
        name: "대여 현황",
        iconType: "mui-icon",
        icon: <CleaningServicesIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
        path: "/robotcleanerrentalstatus",
        adminOnly: true,
      },
    ],
  },
];

// 공통 메뉴 : 모바일 전용
const APPLICATION_RESERVATION_MENU: IMenuItem[] = [
  {
    name: "시설 신청",
    iconType: "mui-icon",
    icon: <InsertInvitationIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservation",
  },
  {
    name: "골프 예약",
    iconType: "mui-icon",
    icon: <AccessTimeIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationscreen",
  },
  {
    name: "북카페 예약",
    iconType: "mui-icon",
    icon: <LocalCafeRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationbookcafe/reserve",
  },
  {
    name: "탁구장 예약",
    iconType: "mui-icon",
    icon: <SportsTennisRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationtabletennis",
  },
  {
    name: "창문 청소기 예약",
    iconType: "mui-icon",
    icon: <CleaningServicesIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationrobotcleaner",
  },
];

const APPLICATION_CONFIRMATION_MENU: IMenuItem[] = [
  {
    name: "신청 확인",
    iconType: "img",
    icon: "/images/icon-mypage.png",
    path: "/reservationend",
  },
  {
    name: "골프 예약 확인",
    iconType: "mui-icon",
    icon: <AlarmOnIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationscreenend",
  },
  {
    name: "북카페 예약 확인",
    iconType: "mui-icon",
    icon: <LocalCafeRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationbookcafeend",
  },
  {
    name: "탁구장 예약 확인",
    iconType: "mui-icon",
    icon: <SportsTennisRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationtabletennisend",
  },
  {
    name: "창문 청소기 예약 확인",
    iconType: "mui-icon",
    icon: <CleaningServicesIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationrobotcleanerend",
  },
];

const COMMUNITY_SERVICE_MENU: IMenuItem[] = [
  {
    name: "북카페",
    iconType: "mui-icon",
    icon: <LocalCafeRoundedIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationbookcafe",
  },
  {
    name: "작은 도서관",
    iconType: "mui-icon",
    icon: <MenuBookIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/mobilebooktemplate",
  },
  {
    name: "도서 대여 현황",
    iconType: "mui-icon",
    icon: <QueryStatsIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/mobilebookstats",
  },
  {
    name: "청구서 확인",
    iconType: "mui-icon",
    icon: <RequestQuoteIcon sx={{ width: "16px", height: "16px", color: "#aec5fe" }} />,
    path: "/reservationaccount",
  },
];

export const MOBILE_MENU: IMenuItem[] = [
  ...APPLICATION_RESERVATION_MENU,
  ...APPLICATION_CONFIRMATION_MENU,
  ...COMMUNITY_SERVICE_MENU,
];

const MOBILE_MENU_GROUPS: Omit<IMenuGroup, "access">[] = [
  {
    name: "신청·예약",
    menuList: APPLICATION_RESERVATION_MENU,
  },
  {
    name: "신청·예약 확인",
    menuList: APPLICATION_CONFIRMATION_MENU,
  },
  {
    name: "커뮤니티 생활·정산",
    menuList: COMMUNITY_SERVICE_MENU,
  },
];

export const SUPER_ADMIN_MENU: IMenuItem[] = [
  {
    name: "시설 예약 관리",
    iconType: "mui-icon",
    icon: <SummarizeIcon sx={{ width: "16px", height: "16px", color: "#ffb74d" }} />,
    path: "/screenadmin",
  },
  {
    name: "커뮤니티 대시보드",
    iconType: "mui-icon",
    icon: <QueryStatsIcon sx={{ width: "16px", height: "16px", color: "#ffb74d" }} />,
    path: "/adminstats",
  },
  {
    name: "도서 등록",
    iconType: "mui-icon",
    icon: <MenuBookIcon sx={{ width: "16px", height: "16px", color: "#ffb74d" }} />,
    path: "/adminbook",
  },
  {
    name: "바코드 등록",
    iconType: "mui-icon",
    icon: <QrCodeScannerRoundedIcon sx={{ width: "16px", height: "16px", color: "#ffb74d" }} />,
    path: "/adminbookbarcode",
  },
];

export const MENU_GROUPS: IMenuGroup[] = [
  ...MANAGEMENT_MENU_GROUPS.map((menuGroup) => ({ ...menuGroup, access: "management" as const })),
  ...MOBILE_MENU_GROUPS.map((menuGroup) => ({ ...menuGroup, access: "authenticated" as const })),
  {
    name: "시스템 관리",
    menuList: SUPER_ADMIN_MENU,
    access: "superAdmin",
  },
];

const MY_PAGE_MENU: IMenuItem = {
  name: "마이페이지",
  iconType: "mui-icon",
  icon: <AccountCircleRoundedIcon />,
  path: "/mobilemypage",
};

const MENU_ITEM_ACCENTS = ["#8f5cf6", "#ed5c72", "#5b8ff9", "#f49a5b", "#63c7b2"];

const mainApi = new HttpMainApi();
const CLICK_LOG_EVENT_BY_PATH: Record<string, string> = {
  "/reservationbookcafe": "BOOK_CAFE_GUIDE_CLICK",
  "/reservationbookcafe/reserve": "BOOK_CAFE_MENU_CLICK",
  "/reservationrobotcleaner": "ROBOT_CLEANER_MENU_CLICK",
  "/reservationscreen": "SCREEN_GOLF_MENU_CLICK",
  "/mobilebooktemplate": "SMALL_LIBRARY_MENU_CLICK",
};

const LeftMenu = (props: propsType, _ref: any) => {
  const popCreatePressRef: any = useRef(); // 보도자료 생성용 팝업

  const isSelectedMenu = (path: string) => {
    if (path === "/adminstats") return props.selectedMenu.startsWith("/adminstats");
    if (path === "/reservationbookcafe") {
      return props.selectedMenu === path || props.selectedMenu.startsWith(`${path}/guide/`);
    }
    return path === props.selectedMenu;
  };

  const sendMenuClickLog = (menuInfo: IMenuItem) => {
    const eventType = CLICK_LOG_EVENT_BY_PATH[menuInfo.path];
    if (!eventType) return;

    try {
      void mainApi
        .put_dashboard_event({
          eventType,
          userId: props.userState.userId,
          userName: props.userState.userName,
          userDong: props.userState.userDong,
          userHo: props.userState.userHo,
          path: menuInfo.path,
          source: "LEFT_MENU",
        })
        .catch(() => undefined);
    } catch {
      // Logging must never block menu navigation.
    }
  };

  const moveMenu = (menuInfo: IMenuItem) => {
    sendMenuClickLog(menuInfo);
    props.move(menuInfo.path);
  };

  const canViewManagement =
    props.userState.userLevel === "관리자" || props.userState.userId === "admin" || props.userState.userId === "rosijin";

  const canViewGroup = (menuGroup: IMenuGroup) => {
    if (menuGroup.access === "management") return canViewManagement;
    if (menuGroup.access === "superAdmin") return props.userState.userId === "rosijin";
    return props.userState.isAuth;
  };

  // 사이트 로고 동적 할당
  function SiteLogo() {
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      props.move("/mobilemain");
    };

    return (
      <h1 className="left-menu-logo-root">
        <a
          href="/mobilemain"
          className="left-menu-logo-link"
          aria-label="홈으로 이동"
          onClick={handleClick}
        >
          <HomeRoundedIcon className="left-menu-home-icon" />
        </a>
      </h1>
    );
  }

  const renderMenuItem = (menuInfo: IMenuItem, index: number, groupIndex: number, description?: string) => (
    <ListItem className="left-menu-item" disablePadding key={menuInfo.name}>
      <ListItemButton
        className="left-menu-wrapper"
        selected={isSelectedMenu(menuInfo.path)}
        onClick={() => moveMenu(menuInfo)}
      >
        <ListItemIcon
          className="left-menu-item-icon"
          sx={{ color: MENU_ITEM_ACCENTS[(groupIndex + index) % MENU_ITEM_ACCENTS.length] }}
        >
          {menuInfo.iconType === "img" && (
            <CardMedia component="img" className="left-menu-icon" image={menuInfo.icon} alt="" />
          )}
          {menuInfo.iconType === "mui-icon" && <Icon>{menuInfo.icon}</Icon>}
        </ListItemIcon>
        <ListItemText
          primary={<Typography className="left-menu-text" noWrap>{menuInfo.name}</Typography>}
          secondary={description}
          secondaryTypographyProps={{ sx: { fontSize: 12, color: "#62596c", mt: 0.5 } }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box className="left-menu-shell">
      <Box component="header" className="left-menu-header">
        <SiteLogo />
        <IconButton className="left-menu-close" aria-label="메뉴 닫기" onClick={props.closeMenu}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Typography component="p" className="left-menu-page-title">
        전체메뉴
      </Typography>
      <Box component="nav" aria-label="전체 메뉴" className="left-menu-root">
        {MENU_GROUPS.filter(canViewGroup).map((menuGroup, groupIndex) => {
          const visibleMenuList = menuGroup.menuList.filter(
            (menuInfo) => !menuInfo.adminOnly || props.userState.isAuth,
          );

          if (visibleMenuList.length === 0) return null;

          return (
            <Box component="section" className="left-menu-group" key={menuGroup.name}>
              <Typography component="h2" className="left-menu-group-title">
                {menuGroup.name}
              </Typography>
              <List className="left-menu-grid" disablePadding>
                {visibleMenuList.map((menuInfo, index) => renderMenuItem(menuInfo, index, groupIndex))}
              </List>
            </Box>
          );
        })}
        {props.userState.isAuth ? (
          <List className="left-menu-account" disablePadding>
            {renderMenuItem(MY_PAGE_MENU, 0, 0, "회원정보 · 비밀번호 변경 · 로그아웃")}
          </List>
        ) : null}
      </Box>
      <Box sx={{ flexGrow: "1" }}></Box>
      <PopCreatePress ref={popCreatePressRef} userState={props.userState} move={props.move} />
    </Box>
  );
};

export default forwardRef(LeftMenu);
